import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_COLORS, getLangName, getLangRequirement } from '../../data';
import { useBuilderContext } from '../../context/BuilderContext';
import { useGdprOuKey } from '@/hooks/useOUs';
import { translationApi, SessionExpiredError } from '@/api/client';

export function TranslationsSection() {
  const { t } = useTranslation();
  const { form, setForm } = useBuilderContext();
  const gdprOuKey = useGdprOuKey();
  const { mandatory, optional } = getLangRequirement(form, gdprOuKey);

  // 'es' se agrega como pestaña solo si alguna pregunta fue redactada en otro idioma —
  // en ese caso español pasa a ser un idioma "faltante" para esa pregunta puntual.
  const needsEsTab = form.questions.some(q => q.sourceLang !== 'es');
  const transLangs = useMemo(
    () => [...(needsEsTab ? ['es'] : []), ...mandatory, ...optional],
    [needsEsTab, mandatory, optional],
  );
  const mandatorySet = useMemo(() => new Set(needsEsTab ? [...mandatory, 'es'] : mandatory), [mandatory, needsEsTab]);

  const [activeLang, setActiveLang] = useState(transLangs[0] ?? 'en');
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const getRequiredTrlKeys = (lang: string): string[] => {
    const keys: string[] = ['survey_name'];
    form.questions.forEach((q, qi) => {
      if (q.sourceLang === lang) return; // esta pregunta ya está redactada en este idioma
      keys.push(`q_${qi}`);
      const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
      if (hasOpts) q.options.forEach((_, oi) => keys.push(`q_${qi}_opt_${oi}`));
    });
    return keys;
  };

  const getLangCompleteness = (lang: string) => {
    const enabled = form.translationsEnabled[lang] !== false;
    const required = getRequiredTrlKeys(lang);
    const total = required.length;
    const filled = required.filter(k => form.translations?.[lang]?.[k]?.trim()).length;
    return { enabled, filled, total, complete: enabled && total > 0 && filled === total };
  };

  const updateTrl = (lang: string, key: string, val: string) => {
    setForm(p => ({
      ...p,
      translations: {
        ...p.translations,
        [lang]: { ...(p.translations[lang] || {}), [key]: val },
      },
    }));
  };

  /** Texto original + su idioma para una clave de traducción dada, o null si esa clave no aplica
   *  al idioma activo (p.ej. una pregunta cuyo sourceLang ya es el idioma de la pestaña). */
  function resolveSourceText(key: string, lang: string): { text: string; sourceLang: string } | null {
    if (key === 'survey_name') return { text: form.name, sourceLang: 'es' };
    if (key === 'survey_desc') return { text: form.description, sourceLang: 'es' };
    const match = /^q_(\d+)(?:_opt_(\d+))?$/.exec(key);
    if (!match) return null;
    const qi = Number(match[1]);
    const question = form.questions[qi];
    if (!question || question.sourceLang === lang) return null;
    if (match[2] === undefined) return { text: question.text, sourceLang: question.sourceLang };
    const optIndex = Number(match[2]);
    return { text: question.options[optIndex] ?? '', sourceLang: question.sourceLang };
  }

  const autoTranslate = async (lang: string) => {
    const keys = getRequiredTrlKeys(lang).filter(key => !form.translations?.[lang]?.[key]?.trim());
    const bySourceLang = new Map<string, { key: string; text: string }[]>();
    for (const key of keys) {
      const resolved = resolveSourceText(key, lang);
      if (!resolved?.text.trim()) continue;
      const bucket = bySourceLang.get(resolved.sourceLang) ?? [];
      bucket.push({ key, text: resolved.text });
      bySourceLang.set(resolved.sourceLang, bucket);
    }
    if (bySourceLang.size === 0) return;

    setTranslating(true);
    setTranslateError(null);
    try {
      for (const [sourceLang, items] of bySourceLang) {
        const response = await translationApi.translateTexts({
          sourceLang,
          targetLangs: [lang],
          texts: items.map(item => item.text),
        });
        const translated = response.translations[lang] ?? [];
        items.forEach((item, index) => {
          const value = translated[index];
          if (value?.trim()) updateTrl(lang, item.key, value);
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('autoTranslate failed', error);
      setTranslateError(
        error instanceof SessionExpiredError
          ? t('admin.sessionExpiredError')
          : t('admin.builderAutoTranslateError'),
      );
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="builder-section">
      <div className="cfg-section-head">
        <div>
          <h3><i className="fas fa-language" /> {t('admin.builderTranslationsTitle')}</h3>
          <p className="cfg-section-sub">{t('admin.builderTranslationsSub')}</p>
        </div>
      </div>

      <div className="info-banner-small" style={{ marginBottom: 14 }}>
        <i className="fas fa-robot" style={{ color: 'var(--cp-purple)' }} />
        <p>{t('admin.builderAutoTranslateNote')}</p>
      </div>

      {translateError && (
        <div className="info-banner-small info-banner-error" style={{ marginBottom: 14 }}>
          <i className="fas fa-triangle-exclamation" />
          <p>{translateError}</p>
        </div>
      )}

      {/* Tabs de idiomas */}
      <div className="lang-tabs-bar">
        {transLangs.map(lang => {
          const { enabled, filled, total, complete } = getLangCompleteness(lang);
          const partial = enabled && filled > 0 && filled < total;
          return (
            <button
              key={lang}
              className={`lang-tab-btn${activeLang === lang ? ' active' : ''}`}
              onClick={() => setActiveLang(lang)}
            >
              <i className="fas fa-globe" /> {getLangName(lang, t)}
              {mandatorySet.has(lang) && <span className="lang-tab-badge required">{t('admin.builderLangRequired')}</span>}
              {!mandatorySet.has(lang) && <span className="lang-tab-badge optional">{t('admin.builderLangOptional')}</span>}
              {!enabled
                ? <span className="lang-tab-badge disabled">{t('admin.builderLangNotApplicable')}</span>
                : complete
                ? <span className="lang-tab-badge complete"><i className="fas fa-check" /> {t('admin.builderLangComplete')}</span>
                : partial
                ? <span className="lang-tab-badge partial">{filled}/{total}</span>
                : <span className="lang-tab-badge empty">{t('admin.builderLangPending')}</span>
              }
            </button>
          );
        })}
      </div>

      {/* Panel activo */}
      {transLangs.map(lang => {
        if (lang !== activeLang) return null;
        const enabled = form.translationsEnabled[lang] !== false;
        const isMandatory = mandatorySet.has(lang);
        const langColor = LANG_COLORS[lang] || '#342F82';
        return (
          <div key={lang} className="lang-panel">
            <div className="lang-panel-header">
              <div className="lang-panel-title">
                <i className="fas fa-language" /> {getLangName(lang, t)}
                {isMandatory && (
                  <span className="cfg-admin-tag" style={{ marginLeft: 6 }}>
                    {t('admin.builderLangRequired')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {enabled && (
                  <button
                    className="trl-auto-btn"
                    onClick={() => void autoTranslate(lang)}
                    disabled={translating}
                  >
                    {translating
                      ? <><i className="fas fa-spinner fa-spin" /> {t('admin.builderAutoTranslating')}</>
                      : <><i className="fas fa-robot" /> {t('admin.builderAutoTranslateBtn')}</>}
                  </button>
                )}
                {isMandatory ? (
                  <span className="lang-toggle-applies lang-toggle-locked">
                    <i className="fas fa-lock" /> {t('admin.builderLangApplies')}
                  </span>
                ) : (
                  <label className="lang-toggle-label">
                    <span className={enabled ? 'lang-toggle-applies' : 'lang-toggle-noapply'}>
                      {enabled
                        ? <><i className="fas fa-check-circle" /> {t('admin.builderLangApplies')}</>
                        : <><i className="fas fa-times-circle" /> {t('admin.builderLangDoesNotApply')}</>}
                    </span>
                    <div
                      className={`lang-toggle-switch${enabled ? ' on' : ''}`}
                      onClick={() => setForm(p => ({
                        ...p,
                        translationsEnabled: { ...p.translationsEnabled, [lang]: !enabled },
                      }))}
                    >
                      <div className="lang-toggle-knob" />
                    </div>
                  </label>
                )}
              </div>
            </div>

            {enabled ? (
              <div className="trl-questions-list">
                {(() => {
                  const trlKey = 'survey_name';
                  const val = form.translations?.[lang]?.[trlKey] || '';
                  const done = val.trim().length > 0;
                  return (
                    <div className={`trl-row${done ? ' trl-row--done' : ''}`}>
                      <div className="trl-row-num">
                        <i className="fas fa-tag" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                      </div>
                      <div className="trl-row-body">
                        <div className="trl-original">
                          <span className="trl-lang-pill es">ES</span>
                          <span className="trl-original-text trl-field-label">
                            {t('admin.builderTrlName')}{' '}
                            <em>{form.name || <span style={{ color: '#ccc' }}>{t('admin.builderTrlEmpty')}</span>}</em>
                          </span>
                        </div>
                        <div className="trl-input-wrap">
                          <span className="trl-lang-pill" style={{ background: langColor }}>
                            {lang.toUpperCase()}
                          </span>
                          <input
                            type="text"
                            className="trl-input"
                            value={val}
                            placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                            onChange={e => updateTrl(lang, trlKey, e.target.value)}
                          />
                          {done && <i className="fas fa-check-circle trl-check" />}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {form.description && (() => {
                  const trlKey = 'survey_desc';
                  const val = form.translations?.[lang]?.[trlKey] || '';
                  const done = val.trim().length > 0;
                  return (
                    <div className={`trl-row${done ? ' trl-row--done' : ''}`}>
                      <div className="trl-row-num">
                        <i className="fas fa-align-left" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                      </div>
                      <div className="trl-row-body">
                        <div className="trl-original">
                          <span className="trl-lang-pill es">ES</span>
                          <span className="trl-original-text trl-field-label">
                            {t('admin.builderTrlDesc')} <em>{form.description}</em>
                          </span>
                        </div>
                        <div className="trl-input-wrap">
                          <span className="trl-lang-pill" style={{ background: langColor }}>
                            {lang.toUpperCase()}
                          </span>
                          <input
                            type="text"
                            className="trl-input"
                            value={val}
                            placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                            onChange={e => updateTrl(lang, trlKey, e.target.value)}
                          />
                          {done && <i className="fas fa-check-circle trl-check" />}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {form.questions.map((q, qi) => {
                  if (q.sourceLang === lang) return null;
                  const qKey = `q_${qi}`;
                  const qVal = form.translations?.[lang]?.[qKey] || '';
                  const qDone = qVal.trim().length > 0;
                  const hasOpts = q.type === 'multiple' || (q.type === 'behaviors' && q.enfoque === 'individual');
                  return (
                    <div key={q.id} style={{ display: 'contents' }}>
                      <div className={`trl-row${qDone ? ' trl-row--done' : ''}`}>
                        <div className="trl-row-num">{qi + 1}</div>
                        <div className="trl-row-body">
                          <div className="trl-original">
                            <span className="trl-lang-pill" style={{ background: LANG_COLORS[q.sourceLang] ?? 'var(--cp-green)' }}>
                              {q.sourceLang.toUpperCase()}
                            </span>
                            <span className="trl-original-text">
                              {q.text || <em style={{ color: '#ccc' }}>{t('admin.builderTrlNoText')}</em>}
                            </span>
                          </div>
                          <div className="trl-input-wrap">
                            <span className="trl-lang-pill" style={{ background: langColor }}>
                              {lang.toUpperCase()}
                            </span>
                            <input
                              type="text"
                              className="trl-input"
                              value={qVal}
                              placeholder={`${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                              onChange={e => updateTrl(lang, qKey, e.target.value)}
                            />
                            {qDone && <i className="fas fa-check-circle trl-check" />}
                          </div>
                        </div>
                      </div>

                      {hasOpts && q.options.length > 0 && (
                        <div className="trl-opts-list">
                          {q.options.map((opt, oi) => {
                            const optKey = `q_${qi}_opt_${oi}`;
                            const optVal = form.translations?.[lang]?.[optKey] || '';
                            const optDone = optVal.trim().length > 0;
                            return (
                              <div key={oi} className={`trl-row trl-row--opt${optDone ? ' trl-row--done' : ''}`}>
                                <div className="trl-row-num">
                                  <i className="fas fa-list-ul" style={{ fontSize: '0.65rem', opacity: 0.5 }} />
                                </div>
                                <div className="trl-row-body">
                                  <div className="trl-original">
                                    <span className="trl-lang-pill" style={{ background: LANG_COLORS[q.sourceLang] ?? 'var(--cp-green)' }}>
                                      {q.sourceLang.toUpperCase()}
                                    </span>
                                    <span className="trl-original-text" style={{ fontSize: '0.74rem' }}>
                                      {opt || <em style={{ color: '#ccc' }}>{t('admin.builderTrlEmpty')}</em>}
                                    </span>
                                  </div>
                                  <div className="trl-input-wrap">
                                    <span className="trl-lang-pill" style={{ background: langColor }}>
                                      {lang.toUpperCase()}
                                    </span>
                                    <input
                                      type="text"
                                      className="trl-input"
                                      value={optVal}
                                      placeholder={`${t('admin.builderTrlPlaceholderOpt')} ${oi + 1} ${t('admin.builderTrlPlaceholderIn')} ${getLangName(lang, t)}…`}
                                      onChange={e => updateTrl(lang, optKey, e.target.value)}
                                    />
                                    {optDone && <i className="fas fa-check-circle trl-check" />}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="trl-noapply-msg">
                <i className="fas fa-info-circle" /> {t('admin.builderLangDisabled')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
